/**
 * Regtank liveness verification hook
 * Handles liveness URL generation, status checking, and verification flow
 *
 * Refactored to use shared document signing hooks for consistency:
 * - useRegtankLivenessSession: Manages session extraction and liveness data
 * - useRegtankLivenessValidation: Handles validation logic (canAttempt, shouldVerify, isDisabled)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { isValidRegtankUrl } from "@funded-here-interface/common/src/Utils/urlValidation";
import { LivenessWebVerifyStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import { setRegtankLiveness } from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { MESSAGES, useButtonState } from "../../../components/DocumentSigning";
import useGetLivenessWebVerifyLink from "../../../hooks/useGetLivenessWebVerifyLink";
import useGetRegtankLivenessWebVerifyStatus from "../../../hooks/useGetRegtankLivenessWebVerifyStatus";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUser";
import { useRegtankLivenessSession } from "../../../hooks/useRegtankLivenessSession";
import { useRegtankLivenessValidation } from "../../../hooks/useRegtankLivenessValidation";
import { saveSubscriptionToSession } from "../utils/sessionStorage";
import { TOAST_IDS } from "../StructureNote.constants";
import { LivenessVerifyParams } from "../StructureNote.types";
import { AGREEMENT_NAME } from "../../../components/DocumentSigning/constants";

interface UseRegtankLivenessProps {
  token: string;
  documentId: number;
  regtankLivenessFeatureFlag: boolean;
  regtankOnboardingFeatureFlag: boolean | null;
  regtankOnboarding: boolean | null;
  regtankRequest: any;
  regtankLiveness?: any[];
  onSignSuccess: () => void;
  onViewerKeyIncrement: () => void;
  investorAmount: number;
  structuredNoteId?: number;
}

/**
 * Hook to manage Regtank liveness verification flow
 * Now uses shared hooks for session management and validation
 *
 * @param props - Hook configuration
 * @returns Liveness state and handler functions
 */
export const useRegtankLiveness = ({
  token,
  documentId,
  regtankLivenessFeatureFlag,
  regtankOnboardingFeatureFlag,
  regtankOnboarding,
  regtankRequest,
  regtankLiveness,
  onSignSuccess,
  onViewerKeyIncrement,
  investorAmount,
  structuredNoteId,
}: UseRegtankLivenessProps) => {
  const [regtankLivenessUrl, setRegtankLivenessUrl] = useState("");
  const [showFailedModal, setShowFailedModal] = useState(false);

  // Track which sessions we've already checked to prevent infinite loops
  const checkedSessionsRef = useRef<Set<string>>(new Set());

  const dispatch = useDispatch();
  const livenessLinkMutation = useGetLivenessWebVerifyLink();
  const livenessStatusMutation = useGetRegtankLivenessWebVerifyStatus();
  const livenessStatusMutateRef = useRef(livenessStatusMutation.mutate);
  livenessStatusMutateRef.current = livenessStatusMutation.mutate;
  const { data: userData } = useGetUser(token);
  const redirectUrl = window.location.href;

  // Use shared hooks for session management and validation
  const allLivenessData = userData?.user?.regtankLiveness || regtankLiveness;
  const livenessSession = useRegtankLivenessSession(
    documentId,
    allLivenessData
  );
  const livenessValidation = useRegtankLivenessValidation(livenessSession);

  // Use shared button state hook for consistent button disabled/title logic
  const { buttonDisabled, titleButton, disableButton } = useButtonState({
    regtankLivenessFeatureFlag,
    regtankOnboardingFeatureFlag,
    regtankOnboarding,
    regtankRequest,
    isDisabledByLiveness: livenessValidation.isDisabled,
  });

  /**
   * Generate liveness verification URL
   */
  const generateLivenessUrl = useCallback(
    (params: LivenessVerifyParams) => {
      if (!structuredNoteId) {
        return;
      }
      toast.info(MESSAGES.LOADING_LIVENESS, {
        toastId: TOAST_IDS.LOADING_LIVENESS,
      });
      livenessLinkMutation.mutate(
        {
          token,
          documentId,
          redirectUrl,
          agreementName: AGREEMENT_NAME.STRUCTURE_NOTE_SUBSCRIPTION_AGREEMENT,
          investorAmount,
          structuredNoteId,
        },
        {
          onSuccess: (data) => {
            if (!isValidRegtankUrl(data.verifyLink)) {
              toast.error(MESSAGES.INVALID_URL);
              onViewerKeyIncrement();
              return;
            }

            disableButton(MESSAGES.VERIFICATION_DISABLED);

            // Save subscription state to session storage
            saveSubscriptionToSession({
              category: "subscribe",
              realUrl: params.realUrl,
              documentId,
              completedAt: params.completedAt,
              investorSubscribeAmount: params.investorSubscribeAmount,
              selectedData: params.selectedData,
              sessionId: data.sessionId,
            });

            // Update Redux state
            dispatch(
              setRegtankLiveness({
                regtankLiveness: [
                  {
                    id: documentId.toString(),
                    clientSessionId: documentId.toString(),
                    sessionId: data.sessionId,
                    livenessStatus: null,
                    regtankLivenessUrl: data.verifyLink,
                    noOfAttemptLiveness: 0,
                    reattemptApproval: null,
                    expiredDate: data.expiredDate,
                  },
                ],
              })
            );

            window.open(data.verifyLink, "_self");
            onViewerKeyIncrement();
          },
          onError: (e) => {
            toast.error((e as Error).message);
            onViewerKeyIncrement();
          },
        }
      );
    },
    [
      token,
      documentId,
      redirectUrl,
      dispatch,
      livenessLinkMutation,
      onViewerKeyIncrement,
      disableButton,
      investorAmount,
      structuredNoteId,
    ]
  );

  /**
   * Main liveness verification handler
   */
  const handleLivenessVerify = useCallback(
    (params: LivenessVerifyParams) => {
      if (!regtankLivenessFeatureFlag) {
        onSignSuccess();
        return;
      }

      // Use existing liveness URL if available
      if (regtankLivenessUrl) {
        if (!isValidRegtankUrl(regtankLivenessUrl)) {
          toast.error(MESSAGES.INVALID_URL);
          return;
        }
        window.open(regtankLivenessUrl, "_self");
      } else {
        generateLivenessUrl(params);
      }
    },
    [
      regtankLivenessFeatureFlag,
      regtankLivenessUrl,
      generateLivenessUrl,
      onSignSuccess,
    ]
  );

  /**
   * Handle modal close after failed liveness
   */
  const handleModalClose = useCallback(() => {
    disableButton(MESSAGES.VERIFICATION_DISABLED);
    setShowFailedModal(false);
    sessionStorage.removeItem("regtankSessionId");
    onViewerKeyIncrement();
  }, [disableButton, onViewerKeyIncrement]);

  // Verify liveness status when needed (using shared validation hook)
  useEffect(() => {
    if (!regtankLivenessFeatureFlag || !livenessSession) {
      return;
    }

    // Always check status after returning from liveness (even if not marked shouldVerify yet)
    // This handles the case where liveness was completed but status hasn't updated in Redux
    const shouldCheckStatus =
      livenessValidation.shouldVerify ||
      (livenessSession.latestLiveness.livenessStatus === null &&
        livenessSession.sessionId);

    if (!shouldCheckStatus) {
      return;
    }

    // Prevent infinite loops by only checking each session once
    const sessionId = livenessSession.sessionId;
    if (checkedSessionsRef.current.has(sessionId)) {
      return;
    }

    checkedSessionsRef.current.add(sessionId);

    livenessStatusMutateRef.current(
      { token, sessionId },
      {
        onSuccess: (data: any) => {
          if (data === LivenessWebVerifyStatusEnum.SUCCESS) {
            // Update Redux state with successful status
            dispatch(
              setRegtankLiveness({
                regtankLiveness: [
                  {
                    id: documentId.toString(),
                    ...livenessSession.latestLiveness,
                    livenessStatus: LivenessWebVerifyStatusEnum.SUCCESS,
                  },
                ],
              })
            );
          } else if (
            data === LivenessWebVerifyStatusEnum.LIVENESS_FAILED ||
            data === LivenessWebVerifyStatusEnum.FAILED
          ) {
            // Update Redux state with failed status
            dispatch(
              setRegtankLiveness({
                regtankLiveness: [
                  {
                    id: documentId.toString(),
                    ...livenessSession.latestLiveness,
                    livenessStatus: data,
                  },
                ],
              })
            );
            setShowFailedModal(true);
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  }, [
    livenessSession,
    livenessValidation.shouldVerify,
    token,
    regtankLivenessFeatureFlag,
    dispatch,
    documentId,
  ]);

  // Set liveness URL when user can attempt verification (using shared validation hook)
  useEffect(() => {
    if (livenessValidation.canAttempt && livenessSession?.regtankLivenessUrl) {
      setRegtankLivenessUrl(livenessSession.regtankLivenessUrl);
    }
  }, [livenessValidation.canAttempt, livenessSession]);

  return {
    buttonDisabled,
    titleButton,
    showFailedModal,
    regtankLivenessUrl,
    handleLivenessVerify,
    handleModalClose,
    isLoading:
      livenessLinkMutation.isLoading || livenessStatusMutation.isLoading,
  };
};
